import React from "react";
import { ChartData, chartColors } from "../Dashboard";
import { Box, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Tooltip,
  XAxis,
  YAxis,
  LabelProps,
} from "recharts";
import { isEmpty, isNil } from "ramda";
import ProgressIndicator from "../../../common/progressIndicator/ProgressIndicator";

interface BarChartProps {
  isVerticalChart?: boolean;
  data?: ChartData[];
  chartWidth: number;
  chartHeight: number;
  loading: boolean;
}

const classes = {
  chartText: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    marginRight: "5px",
  },
};

const CustomLabel = (props: LabelProps) => {
  const { x, y, width, height, name } = props;
  const centerY = (y as number) + (height as number) / 2;

  return (
    <text
      x={((x as number) + (width as number)) / 2}
      y={centerY}
      fill="#fff"
      textAnchor="middle"
      dy="0.3em"
      fontSize={12}
      width={width}
    >
      {name || ""}
    </text>
  );
};

const BarChartComponent = (props: BarChartProps) => {
  const { data, chartHeight, chartWidth, isVerticalChart, loading } = props;
  const total = data?.reduce((acc, { count }) => acc + (count as number), 0);

  return (
    <Box padding={0} marginLeft={0} marginTop={1} marginBottom={2}>
      {loading || isNil(data) ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={chartHeight}
        >
          <ProgressIndicator noPadding />
        </Box>
      ) : isEmpty(data) ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={chartHeight}
        >
          No data available
        </Box>
      ) : (
        <>
          <Box 
            width="100%" 
            display="flex" 
            justifyContent="center"
            overflow="hidden"
          >
            <BarChart
              width={chartWidth}
              height={chartHeight}
              data={data}
              layout={isVerticalChart ? "vertical" : "horizontal"}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
            <CartesianGrid strokeDasharray="2 2" />

            {isVerticalChart ? (
              <>
                <XAxis type="number" domain={[0, "dataMax"]} fontSize={12} />
                <YAxis
                  tick={false}
                  dataKey="name"
                  type="category"
                  fontSize={12}
                />
              </>
            ) : (
              <>
                <XAxis dataKey="name" fontSize={12} />
                <YAxis type="number" domain={[0, "dataMax"]} fontSize={12} />
              </>
            )}

            <Tooltip
              cursor={{ fill: "rgba(239, 239, 239, 0.3)" }}
              contentStyle={{
                backgroundColor: "#fff",
                borderRadius: "5px",
                borderColor: "#efefef",
                borderWidth: "1px",
                fontSize: "13px",
              }}
              labelStyle={{ color: "#555", fontWeight: 600 }}
              formatter={(value: unknown) => {
                const percentage = (
                  (Number(value) / (total as number)) *
                  100
                ).toFixed(0);
                return [`Total: ${value} (${percentage}%)`];
              }}
            />

            {isVerticalChart ? (
              <Bar dataKey="count" label={<CustomLabel />}>
                {/* <LabelList dataKey="name" content={<CustomLabel />} /> */}
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={chartColors[index % 20]} />
                ))}
              </Bar>
            ) : (
              <Bar dataKey="count" fill={chartColors[0]} width={20}>
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={chartColors[index % 20]}
                    // width={40}
                  />
                ))}
              </Bar>
            )}
          </BarChart>
          </Box>

          <Box
            width="100%"
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="center"
            margin="15px 0"
          >
            {data.map((item, idx) => (
              <Box
                key={idx}
                display="flex"
                alignItems="center"
                marginRight={2}
                marginTop={1}
              >
                <Box
                  sx={classes.chartText}
                  style={{
                    backgroundColor: chartColors[idx % chartColors.length],
                  }}
                ></Box>

                <Typography>{item.name}</Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default BarChartComponent;
